// TypeScript types for Algo Configs

import { z } from "zod";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DEFAULT_ALGO_CONFIG,
  PRICE_BASIS_OPTIONS,
  PRICE_OFFSET_OPTIONS,
  REFRESH_DISTANCE_OPTIONS,
  MIN_DISTANCE_OPTIONS,
  MAX_DISTANCE_OPTIONS,
  SCALE_TYPE_OPTIONS,
  SCALE_DIRECTION_OPTIONS,
  GRID_SIDE_OPTIONS,
  ChaseOrderConfig,
  ScaleOrderConfig,
  GridConfig,
  ImplementationShortfallConfig,
  AlgoConfig,
} from "./defaults";

import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { Download, Upload, Pencil, Trash, Plus } from "lucide-react";

const ChaseOrderSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  Side: z.string(),
  Type: z.string(),
  TimeInForce: z.string(),
  Size: z.number(),
  SizeRandomRate: z.number(),
  PriceBasis: z.string(),
  ConstantPriceBasis: z.number(),
  PriceBasisMultiplier: z.number(),
  PriceOffset: z.string(),
  PriceOffsetMultiplier: z.number(),
  PriceRandomRate: z.number(),
  CancelDistance: z.string(),
  CancelDistanceMultiplier: z.number(),
  RefreshDistance: z.string(),
  CloseRefreshThresholdMultiplier: z.number(),
  FarRefreshThresholdMultiplier: z.number(),
  MinDistance: z.string(),
  MinDistanceMultiplier: z.number(),
  MaxDistance: z.string(),
  MaxDistanceMultiplier: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  WaitTime: z.number(),
  WaitTimeRandomRate: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  RefreshNumber: z.number(),
  RefreshNumberRandomRate: z.number(),
  FreezingTime: z.number(),
  FreezingTimeRandomRate: z.number(),
  PauseTime: z.number(),
  PauseTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const ScaleOrderSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  Side: z.string(),
  TimeInForce: z.string(),
  TotalSize: z.number(),
  SizeRandomRate: z.number(),
  SizeScaleType: z.string(),
  SizeScaleDirection: z.string(),
  OrdersNumber: z.number(),
  RangeStartBasis: z.string(),
  ConstantRangeStartBasis: z.number(),
  RangeStartBasisMultiplier: z.number(),
  RangeStartOffset: z.string(),
  RangeStartOffsetMultiplier: z.number(),
  RangeEndBasis: z.string(),
  ConstantRangeEndBasis: z.number(),
  RangeEndBasisMultiplier: z.number(),
  RangeEndOffset: z.string(),
  RangeEndOffsetMultiplier: z.number(),
  PriceRandomRate: z.number(),
  CancelDistance: z.string(),
  CancelDistanceMultiplier: z.number(),
  RefreshDistance: z.string(),
  CloseRefreshThresholdMultiplier: z.number(),
  FarRefreshThresholdMultiplier: z.number(),
  MinDistance: z.string(),
  MinDistanceMultiplier: z.number(),
  MaxDistance: z.string(),
  MaxDistanceMultiplier: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  WaitTime: z.number(),
  WaitTimeRandomRate: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  RefreshNumber: z.number(),
  RefreshNumberRandomRate: z.number(),
  FreezingTime: z.number(),
  FreezingTimeRandomRate: z.number(),
  PauseTime: z.number(),
  PauseTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const GridSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  Side: z.string(),
  TimeInForce: z.string(),
  BidTotalSize: z.number(),
  BidSizeRandomRate: z.number(),
  BidSizeScaleType: z.string(),
  BidSizeScaleDirection: z.string(),
  AskTotalSize: z.number(),
  AskSizeRandomRate: z.number(),
  AskSizeScaleType: z.string(),
  AskSizeScaleDirection: z.string(),
  OrdersNumber: z.number(),
  LowerRangeBasis: z.string(),
  ConstantLowerRangeBasis: z.number(),
  LowerRangeBasisMultiplier: z.number(),
  LowerRangeOffset: z.string(),
  LowerRangeOffsetMultiplier: z.number(),
  UpperRangeBasis: z.string(),
  ConstantUpperRangeBasis: z.number(),
  UpperRangeBasisMultiplier: z.number(),
  UpperRangeOffset: z.string(),
  UpperRangeOffsetMultiplier: z.number(),
  StartOrderDistance: z.string(),
  StartOrderDistanceMultiplier: z.number(),
  StopOrderDistance: z.string(),
  StopOrderDistanceMultiplier: z.number(),
  CancelDistance: z.string(),
  LowerRangeCancelDistanceMultiplier: z.number(),
  UpperRangeCancelDistanceMultiplier: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  WaitTime: z.number(),
  WaitTimeRandomRate: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  RefreshNumber: z.number(),
  RefreshNumberRandomRate: z.number(),
  PauseTime: z.number(),
  PauseTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const ImplementationShortfallSchema = z.object({
  AlgoOrderID: z.number(),
  UseAlgoOrder: z.boolean(),
  Hotkey: z.string(),
  MinSize: z.number(),
  MaxSize: z.number(),
  SizeRandomRate: z.number(),
  TargetInventory: z.number(),
  InventoryTolerance: z.number(),
  MinPrice: z.number(),
  MaxPrice: z.number(),
  OrderThrowDepth: z.number(),
  SkewThreshold: z.number(),
  MinZScore: z.number(),
  MaxZScore: z.number(),
  RefreshTime: z.number(),
  RefreshTimeRandomRate: z.number(),
  AutoContinuation: z.boolean(),
});

const AlgoConfigSchema = z.object({
  id: z.string(),
  configName: z.string().min(1, "Name is required"),
  ChaseOrder: ChaseOrderSchema,
  ScaleOrder: ScaleOrderSchema,
  Grid: GridSchema,
  ImplementationShortfall: ImplementationShortfallSchema,
});

import React, { useEffect, useState, useRef } from "react";
import { useConfigsStore } from "./configs.store";
import { useUserStore } from "@/lib/stores/userStore";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function SectionField<T extends object>({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: any;
  onChange: (v: any) => void;
  type?: string;
}) {
  if (type === "checkbox") {
    return (
      <div className="flex items-center gap-2 mb-1 w-full">
        <Checkbox
          checked={!!value}
          onCheckedChange={onChange}
          className="h-4 w-4 min-w-4 min-h-4 max-w-4 max-h-4 border border-input rounded-sm"
        />
        <label className="text-xs font-medium select-none cursor-pointer">
          {label}
        </label>
      </div>
    );
  }
  return (
    <div className="mb-1 w-full">
      <label className="block text-xs font-medium mb-0.5">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) =>
          onChange(type === "number" ? Number(e.target.value) : e.target.value)
        }
        className="h-7 px-2 py-1 text-xs w-full"
      />
    </div>
  );
}

function ChaseOrderForm({
  value,
  onChange,
}: {
  value: ChaseOrderConfig;
  onChange: (v: ChaseOrderConfig) => void;
}) {
  return (
    <div className="border rounded p-2 mb-4 w-full">
      <div className="font-semibold mb-2">Chase Order</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
        />
      </div>
      <div className="grid grid-cols-4 gap-2 w-full">
        <SectionField
          label="Side"
          value={value.Side}
          onChange={(v) => onChange({ ...value, Side: v })}
        />
        <SectionField
          label="Type"
          value={value.Type}
          onChange={(v) => onChange({ ...value, Type: v })}
        />
        <SectionField
          label="TimeInForce"
          value={value.TimeInForce}
          onChange={(v) => onChange({ ...value, TimeInForce: v })}
        />
        <SectionField
          label="Size"
          type="number"
          value={value.Size}
          onChange={(v) => onChange({ ...value, Size: v })}
        />
        <SectionField
          label="SizeRandomRate"
          type="number"
          value={value.SizeRandomRate}
          onChange={(v) => onChange({ ...value, SizeRandomRate: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">PriceBasis</label>
          <Select
            value={value.PriceBasis || PRICE_BASIS_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, PriceBasis: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRICE_BASIS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="ConstantPriceBasis"
          type="number"
          value={value.ConstantPriceBasis}
          onChange={(v) => onChange({ ...value, ConstantPriceBasis: v })}
        />
        <SectionField
          label="PriceBasisMultiplier"
          type="number"
          value={value.PriceBasisMultiplier}
          onChange={(v) => onChange({ ...value, PriceBasisMultiplier: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            PriceOffset
          </label>
          <Select
            value={value.PriceOffset || PRICE_OFFSET_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, PriceOffset: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRICE_OFFSET_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="PriceOffsetMultiplier"
          type="number"
          value={value.PriceOffsetMultiplier}
          onChange={(v) => onChange({ ...value, PriceOffsetMultiplier: v })}
        />
        <SectionField
          label="PriceRandomRate"
          type="number"
          value={value.PriceRandomRate}
          onChange={(v) => onChange({ ...value, PriceRandomRate: v })}
        />
        <SectionField
          label="CancelDistance"
          value={value.CancelDistance}
          onChange={(v) => onChange({ ...value, CancelDistance: v })}
        />
        <SectionField
          label="CancelDistanceMultiplier"
          type="number"
          value={value.CancelDistanceMultiplier}
          onChange={(v) => onChange({ ...value, CancelDistanceMultiplier: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            RefreshDistance
          </label>
          <Select
            value={value.RefreshDistance || REFRESH_DISTANCE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, RefreshDistance: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REFRESH_DISTANCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="CloseRefreshThresholdMultiplier"
          type="number"
          value={value.CloseRefreshThresholdMultiplier}
          onChange={(v) =>
            onChange({ ...value, CloseRefreshThresholdMultiplier: v })
          }
        />
        <SectionField
          label="FarRefreshThresholdMultiplier"
          type="number"
          value={value.FarRefreshThresholdMultiplier}
          onChange={(v) =>
            onChange({ ...value, FarRefreshThresholdMultiplier: v })
          }
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            MinDistance
          </label>
          <Select
            value={value.MinDistance || MIN_DISTANCE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, MinDistance: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MIN_DISTANCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="MinDistanceMultiplier"
          type="number"
          value={value.MinDistanceMultiplier}
          onChange={(v) => onChange({ ...value, MinDistanceMultiplier: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            MaxDistance
          </label>
          <Select
            value={value.MaxDistance || MAX_DISTANCE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, MaxDistance: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MAX_DISTANCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="MaxDistanceMultiplier"
          type="number"
          value={value.MaxDistanceMultiplier}
          onChange={(v) => onChange({ ...value, MaxDistanceMultiplier: v })}
        />
        <SectionField
          label="MinZScore"
          type="number"
          value={value.MinZScore}
          onChange={(v) => onChange({ ...value, MinZScore: v })}
        />
        <SectionField
          label="MaxZScore"
          type="number"
          value={value.MaxZScore}
          onChange={(v) => onChange({ ...value, MaxZScore: v })}
        />
        <SectionField
          label="WaitTime"
          type="number"
          value={value.WaitTime}
          onChange={(v) => onChange({ ...value, WaitTime: v })}
        />
        <SectionField
          label="WaitTimeRandomRate"
          type="number"
          value={value.WaitTimeRandomRate}
          onChange={(v) => onChange({ ...value, WaitTimeRandomRate: v })}
        />
        <SectionField
          label="RefreshTime"
          type="number"
          value={value.RefreshTime}
          onChange={(v) => onChange({ ...value, RefreshTime: v })}
        />
        <SectionField
          label="RefreshTimeRandomRate"
          type="number"
          value={value.RefreshTimeRandomRate}
          onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
        />
        <SectionField
          label="RefreshNumber"
          type="number"
          value={value.RefreshNumber}
          onChange={(v) => onChange({ ...value, RefreshNumber: v })}
        />
        <SectionField
          label="RefreshNumberRandomRate"
          type="number"
          value={value.RefreshNumberRandomRate}
          onChange={(v) => onChange({ ...value, RefreshNumberRandomRate: v })}
        />
        <SectionField
          label="FreezingTime"
          type="number"
          value={value.FreezingTime}
          onChange={(v) => onChange({ ...value, FreezingTime: v })}
        />
        <SectionField
          label="FreezingTimeRandomRate"
          type="number"
          value={value.FreezingTimeRandomRate}
          onChange={(v) => onChange({ ...value, FreezingTimeRandomRate: v })}
        />
        <SectionField
          label="PauseTime"
          type="number"
          value={value.PauseTime}
          onChange={(v) => onChange({ ...value, PauseTime: v })}
        />
        <SectionField
          label="PauseTimeRandomRate"
          type="number"
          value={value.PauseTimeRandomRate}
          onChange={(v) => onChange({ ...value, PauseTimeRandomRate: v })}
        />
      </div>
    </div>
  );
}

function ScaleOrderForm({
  value,
  onChange,
}: {
  value: ScaleOrderConfig;
  onChange: (v: ScaleOrderConfig) => void;
}) {
  return (
    <div className="border rounded p-2 mb-4 w-full">
      <div className="font-semibold mb-2">Scale Order</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
        />
      </div>
      <div className="grid grid-cols-4 gap-2 w-full">
        <SectionField
          label="Side"
          value={value.Side}
          onChange={(v) => onChange({ ...value, Side: v })}
        />
        <SectionField
          label="TimeInForce"
          value={value.TimeInForce}
          onChange={(v) => onChange({ ...value, TimeInForce: v })}
        />
        <SectionField
          label="TotalSize"
          type="number"
          value={value.TotalSize}
          onChange={(v) => onChange({ ...value, TotalSize: v })}
        />
        <SectionField
          label="SizeRandomRate"
          type="number"
          value={value.SizeRandomRate}
          onChange={(v) => onChange({ ...value, SizeRandomRate: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            SizeScaleType
          </label>
          <Select
            value={value.SizeScaleType || SCALE_TYPE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, SizeScaleType: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCALE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            SizeScaleDirection
          </label>
          <Select
            value={value.SizeScaleDirection || SCALE_DIRECTION_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, SizeScaleDirection: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCALE_DIRECTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="OrdersNumber"
          type="number"
          value={value.OrdersNumber}
          onChange={(v) => onChange({ ...value, OrdersNumber: v })}
        />
        <SectionField
          label="RangeStartBasis"
          value={value.RangeStartBasis}
          onChange={(v) => onChange({ ...value, RangeStartBasis: v })}
        />
        <SectionField
          label="ConstantRangeStartBasis"
          type="number"
          value={value.ConstantRangeStartBasis}
          onChange={(v) => onChange({ ...value, ConstantRangeStartBasis: v })}
        />
        <SectionField
          label="RangeStartBasisMultiplier"
          type="number"
          value={value.RangeStartBasisMultiplier}
          onChange={(v) => onChange({ ...value, RangeStartBasisMultiplier: v })}
        />
        <SectionField
          label="RangeStartOffset"
          value={value.RangeStartOffset}
          onChange={(v) => onChange({ ...value, RangeStartOffset: v })}
        />
        <SectionField
          label="RangeStartOffsetMultiplier"
          type="number"
          value={value.RangeStartOffsetMultiplier}
          onChange={(v) =>
            onChange({ ...value, RangeStartOffsetMultiplier: v })
          }
        />
        <SectionField
          label="RangeEndBasis"
          value={value.RangeEndBasis}
          onChange={(v) => onChange({ ...value, RangeEndBasis: v })}
        />
        <SectionField
          label="ConstantRangeEndBasis"
          type="number"
          value={value.ConstantRangeEndBasis}
          onChange={(v) => onChange({ ...value, ConstantRangeEndBasis: v })}
        />
        <SectionField
          label="RangeEndBasisMultiplier"
          type="number"
          value={value.RangeEndBasisMultiplier}
          onChange={(v) => onChange({ ...value, RangeEndBasisMultiplier: v })}
        />
        <SectionField
          label="RangeEndOffset"
          value={value.RangeEndOffset}
          onChange={(v) => onChange({ ...value, RangeEndOffset: v })}
        />
        <SectionField
          label="RangeEndOffsetMultiplier"
          type="number"
          value={value.RangeEndOffsetMultiplier}
          onChange={(v) => onChange({ ...value, RangeEndOffsetMultiplier: v })}
        />
        <SectionField
          label="PriceRandomRate"
          type="number"
          value={value.PriceRandomRate}
          onChange={(v) => onChange({ ...value, PriceRandomRate: v })}
        />
        <SectionField
          label="CancelDistance"
          value={value.CancelDistance}
          onChange={(v) => onChange({ ...value, CancelDistance: v })}
        />
        <SectionField
          label="CancelDistanceMultiplier"
          type="number"
          value={value.CancelDistanceMultiplier}
          onChange={(v) => onChange({ ...value, CancelDistanceMultiplier: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            RefreshDistance
          </label>
          <Select
            value={value.RefreshDistance || REFRESH_DISTANCE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, RefreshDistance: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REFRESH_DISTANCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="CloseRefreshThresholdMultiplier"
          type="number"
          value={value.CloseRefreshThresholdMultiplier}
          onChange={(v) =>
            onChange({ ...value, CloseRefreshThresholdMultiplier: v })
          }
        />
        <SectionField
          label="FarRefreshThresholdMultiplier"
          type="number"
          value={value.FarRefreshThresholdMultiplier}
          onChange={(v) =>
            onChange({ ...value, FarRefreshThresholdMultiplier: v })
          }
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            MinDistance
          </label>
          <Select
            value={value.MinDistance || MIN_DISTANCE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, MinDistance: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MIN_DISTANCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="MinDistanceMultiplier"
          type="number"
          value={value.MinDistanceMultiplier}
          onChange={(v) => onChange({ ...value, MinDistanceMultiplier: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            MaxDistance
          </label>
          <Select
            value={value.MaxDistance || MAX_DISTANCE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, MaxDistance: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MAX_DISTANCE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="MaxDistanceMultiplier"
          type="number"
          value={value.MaxDistanceMultiplier}
          onChange={(v) => onChange({ ...value, MaxDistanceMultiplier: v })}
        />
        <SectionField
          label="MinZScore"
          type="number"
          value={value.MinZScore}
          onChange={(v) => onChange({ ...value, MinZScore: v })}
        />
        <SectionField
          label="MaxZScore"
          type="number"
          value={value.MaxZScore}
          onChange={(v) => onChange({ ...value, MaxZScore: v })}
        />
        <SectionField
          label="WaitTime"
          type="number"
          value={value.WaitTime}
          onChange={(v) => onChange({ ...value, WaitTime: v })}
        />
        <SectionField
          label="WaitTimeRandomRate"
          type="number"
          value={value.WaitTimeRandomRate}
          onChange={(v) => onChange({ ...value, WaitTimeRandomRate: v })}
        />
        <SectionField
          label="RefreshTime"
          type="number"
          value={value.RefreshTime}
          onChange={(v) => onChange({ ...value, RefreshTime: v })}
        />
        <SectionField
          label="RefreshTimeRandomRate"
          type="number"
          value={value.RefreshTimeRandomRate}
          onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
        />
        <SectionField
          label="RefreshNumber"
          type="number"
          value={value.RefreshNumber}
          onChange={(v) => onChange({ ...value, RefreshNumber: v })}
        />
        <SectionField
          label="RefreshNumberRandomRate"
          type="number"
          value={value.RefreshNumberRandomRate}
          onChange={(v) => onChange({ ...value, RefreshNumberRandomRate: v })}
        />
        <SectionField
          label="FreezingTime"
          type="number"
          value={value.FreezingTime}
          onChange={(v) => onChange({ ...value, FreezingTime: v })}
        />
        <SectionField
          label="FreezingTimeRandomRate"
          type="number"
          value={value.FreezingTimeRandomRate}
          onChange={(v) => onChange({ ...value, FreezingTimeRandomRate: v })}
        />
        <SectionField
          label="PauseTime"
          type="number"
          value={value.PauseTime}
          onChange={(v) => onChange({ ...value, PauseTime: v })}
        />
        <SectionField
          label="PauseTimeRandomRate"
          type="number"
          value={value.PauseTimeRandomRate}
          onChange={(v) => onChange({ ...value, PauseTimeRandomRate: v })}
        />
      </div>
    </div>
  );
}

function GridForm({
  value,
  onChange,
}: {
  value: GridConfig;
  onChange: (v: GridConfig) => void;
}) {
  return (
    <div className="border rounded p-2 mb-4 w-full">
      <div className="font-semibold mb-2">Grid</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
        />
      </div>
      <div className="grid grid-cols-4 gap-2 w-full">
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">Side</label>
          <Select
            value={value.Side || GRID_SIDE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, Side: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GRID_SIDE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="TimeInForce"
          value={value.TimeInForce}
          onChange={(v) => onChange({ ...value, TimeInForce: v })}
        />
        <SectionField
          label="BidTotalSize"
          type="number"
          value={value.BidTotalSize}
          onChange={(v) => onChange({ ...value, BidTotalSize: v })}
        />
        <SectionField
          label="BidSizeRandomRate"
          type="number"
          value={value.BidSizeRandomRate}
          onChange={(v) => onChange({ ...value, BidSizeRandomRate: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            BidSizeScaleType
          </label>
          <Select
            value={value.BidSizeScaleType || SCALE_TYPE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, BidSizeScaleType: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCALE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            BidSizeScaleDirection
          </label>
          <Select
            value={
              value.BidSizeScaleDirection || SCALE_DIRECTION_OPTIONS[0].value
            }
            onValueChange={(v) =>
              onChange({ ...value, BidSizeScaleDirection: v })
            }
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCALE_DIRECTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="AskTotalSize"
          type="number"
          value={value.AskTotalSize}
          onChange={(v) => onChange({ ...value, AskTotalSize: v })}
        />
        <SectionField
          label="AskSizeRandomRate"
          type="number"
          value={value.AskSizeRandomRate}
          onChange={(v) => onChange({ ...value, AskSizeRandomRate: v })}
        />
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            AskSizeScaleType
          </label>
          <Select
            value={value.AskSizeScaleType || SCALE_TYPE_OPTIONS[0].value}
            onValueChange={(v) => onChange({ ...value, AskSizeScaleType: v })}
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCALE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mb-1 w-full">
          <label className="block text-xs font-medium mb-0.5">
            AskSizeScaleDirection
          </label>
          <Select
            value={
              value.AskSizeScaleDirection || SCALE_DIRECTION_OPTIONS[0].value
            }
            onValueChange={(v) =>
              onChange({ ...value, AskSizeScaleDirection: v })
            }
          >
            <SelectTrigger className="w-full h-7 text-xs focus:ring-0 focus:ring-offset-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCALE_DIRECTION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SectionField
          label="OrdersNumber"
          type="number"
          value={value.OrdersNumber}
          onChange={(v) => onChange({ ...value, OrdersNumber: v })}
        />
        <SectionField
          label="LowerRangeBasis"
          value={value.LowerRangeBasis}
          onChange={(v) => onChange({ ...value, LowerRangeBasis: v })}
        />
        <SectionField
          label="ConstantLowerRangeBasis"
          type="number"
          value={value.ConstantLowerRangeBasis}
          onChange={(v) => onChange({ ...value, ConstantLowerRangeBasis: v })}
        />
        <SectionField
          label="LowerRangeBasisMultiplier"
          type="number"
          value={value.LowerRangeBasisMultiplier}
          onChange={(v) => onChange({ ...value, LowerRangeBasisMultiplier: v })}
        />
        <SectionField
          label="LowerRangeOffset"
          value={value.LowerRangeOffset}
          onChange={(v) => onChange({ ...value, LowerRangeOffset: v })}
        />
        <SectionField
          label="LowerRangeOffsetMultiplier"
          type="number"
          value={value.LowerRangeOffsetMultiplier}
          onChange={(v) =>
            onChange({ ...value, LowerRangeOffsetMultiplier: v })
          }
        />
        <SectionField
          label="UpperRangeBasis"
          value={value.UpperRangeBasis}
          onChange={(v) => onChange({ ...value, UpperRangeBasis: v })}
        />
        <SectionField
          label="ConstantUpperRangeBasis"
          type="number"
          value={value.ConstantUpperRangeBasis}
          onChange={(v) => onChange({ ...value, ConstantUpperRangeBasis: v })}
        />
        <SectionField
          label="UpperRangeBasisMultiplier"
          type="number"
          value={value.UpperRangeBasisMultiplier}
          onChange={(v) => onChange({ ...value, UpperRangeBasisMultiplier: v })}
        />
        <SectionField
          label="UpperRangeOffset"
          value={value.UpperRangeOffset}
          onChange={(v) => onChange({ ...value, UpperRangeOffset: v })}
        />
        <SectionField
          label="UpperRangeOffsetMultiplier"
          type="number"
          value={value.UpperRangeOffsetMultiplier}
          onChange={(v) =>
            onChange({ ...value, UpperRangeOffsetMultiplier: v })
          }
        />
        <SectionField
          label="StartOrderDistance"
          value={value.StartOrderDistance}
          onChange={(v) => onChange({ ...value, StartOrderDistance: v })}
        />
        <SectionField
          label="StartOrderDistanceMultiplier"
          type="number"
          value={value.StartOrderDistanceMultiplier}
          onChange={(v) =>
            onChange({ ...value, StartOrderDistanceMultiplier: v })
          }
        />
        <SectionField
          label="StopOrderDistance"
          value={value.StopOrderDistance}
          onChange={(v) => onChange({ ...value, StopOrderDistance: v })}
        />
        <SectionField
          label="StopOrderDistanceMultiplier"
          type="number"
          value={value.StopOrderDistanceMultiplier}
          onChange={(v) =>
            onChange({ ...value, StopOrderDistanceMultiplier: v })
          }
        />
        <SectionField
          label="CancelDistance"
          value={value.CancelDistance}
          onChange={(v) => onChange({ ...value, CancelDistance: v })}
        />
        <SectionField
          label="LowerRangeCancelDistanceMultiplier"
          type="number"
          value={value.LowerRangeCancelDistanceMultiplier}
          onChange={(v) =>
            onChange({ ...value, LowerRangeCancelDistanceMultiplier: v })
          }
        />
        <SectionField
          label="UpperRangeCancelDistanceMultiplier"
          type="number"
          value={value.UpperRangeCancelDistanceMultiplier}
          onChange={(v) =>
            onChange({ ...value, UpperRangeCancelDistanceMultiplier: v })
          }
        />
        <SectionField
          label="MinZScore"
          type="number"
          value={value.MinZScore}
          onChange={(v) => onChange({ ...value, MinZScore: v })}
        />
        <SectionField
          label="MaxZScore"
          type="number"
          value={value.MaxZScore}
          onChange={(v) => onChange({ ...value, MaxZScore: v })}
        />
        <SectionField
          label="WaitTime"
          type="number"
          value={value.WaitTime}
          onChange={(v) => onChange({ ...value, WaitTime: v })}
        />
        <SectionField
          label="WaitTimeRandomRate"
          type="number"
          value={value.WaitTimeRandomRate}
          onChange={(v) => onChange({ ...value, WaitTimeRandomRate: v })}
        />
        <SectionField
          label="RefreshTime"
          type="number"
          value={value.RefreshTime}
          onChange={(v) => onChange({ ...value, RefreshTime: v })}
        />
        <SectionField
          label="RefreshTimeRandomRate"
          type="number"
          value={value.RefreshTimeRandomRate}
          onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
        />
        <SectionField
          label="RefreshNumber"
          type="number"
          value={value.RefreshNumber}
          onChange={(v) => onChange({ ...value, RefreshNumber: v })}
        />
        <SectionField
          label="RefreshNumberRandomRate"
          type="number"
          value={value.RefreshNumberRandomRate}
          onChange={(v) => onChange({ ...value, RefreshNumberRandomRate: v })}
        />
        <SectionField
          label="PauseTime"
          type="number"
          value={value.PauseTime}
          onChange={(v) => onChange({ ...value, PauseTime: v })}
        />
        <SectionField
          label="PauseTimeRandomRate"
          type="number"
          value={value.PauseTimeRandomRate}
          onChange={(v) => onChange({ ...value, PauseTimeRandomRate: v })}
        />
      </div>
    </div>
  );
}

function ImplementationShortfallForm({
  value,
  onChange,
}: {
  value: ImplementationShortfallConfig;
  onChange: (v: ImplementationShortfallConfig) => void;
}) {
  return (
    <div className="border rounded p-2 mb-4 w-full">
      <div className="font-semibold mb-2">ImplementationShortfall</div>
      <div className="flex flex-col gap-1 mb-2">
        <SectionField
          label="Enabled"
          type="checkbox"
          value={value.UseAlgoOrder}
          onChange={(v) => onChange({ ...value, UseAlgoOrder: v })}
        />
        <SectionField
          label="Auto Continuation"
          type="checkbox"
          value={value.AutoContinuation}
          onChange={(v) => onChange({ ...value, AutoContinuation: v })}
        />
      </div>
      <div className="grid grid-cols-4 gap-2 w-full">
        <SectionField
          label="MinSize"
          type="number"
          value={value.MinSize}
          onChange={(v) => onChange({ ...value, MinSize: v })}
        />
        <SectionField
          label="MaxSize"
          type="number"
          value={value.MaxSize}
          onChange={(v) => onChange({ ...value, MaxSize: v })}
        />
        <SectionField
          label="SizeRandomRate"
          type="number"
          value={value.SizeRandomRate}
          onChange={(v) => onChange({ ...value, SizeRandomRate: v })}
        />
        <SectionField
          label="TargetInventory"
          type="number"
          value={value.TargetInventory}
          onChange={(v) => onChange({ ...value, TargetInventory: v })}
        />
        <SectionField
          label="InventoryTolerance"
          type="number"
          value={value.InventoryTolerance}
          onChange={(v) => onChange({ ...value, InventoryTolerance: v })}
        />
        <SectionField
          label="MinPrice"
          type="number"
          value={value.MinPrice}
          onChange={(v) => onChange({ ...value, MinPrice: v })}
        />
        <SectionField
          label="MaxPrice"
          type="number"
          value={value.MaxPrice}
          onChange={(v) => onChange({ ...value, MaxPrice: v })}
        />
        <SectionField
          label="OrderThrowDepth"
          type="number"
          value={value.OrderThrowDepth}
          onChange={(v) => onChange({ ...value, OrderThrowDepth: v })}
        />
        <SectionField
          label="SkewThreshold"
          type="number"
          value={value.SkewThreshold}
          onChange={(v) => onChange({ ...value, SkewThreshold: v })}
        />
        <SectionField
          label="MinZScore"
          type="number"
          value={value.MinZScore}
          onChange={(v) => onChange({ ...value, MinZScore: v })}
        />
        <SectionField
          label="MaxZScore"
          type="number"
          value={value.MaxZScore}
          onChange={(v) => onChange({ ...value, MaxZScore: v })}
        />
        <SectionField
          label="RefreshTime"
          type="number"
          value={value.RefreshTime}
          onChange={(v) => onChange({ ...value, RefreshTime: v })}
        />
        <SectionField
          label="RefreshTimeRandomRate"
          type="number"
          value={value.RefreshTimeRandomRate}
          onChange={(v) => onChange({ ...value, RefreshTimeRandomRate: v })}
        />
      </div>
    </div>
  );
}

function ConfigForm({
  initialConfig,
  onSave,
  onCancel,
}: {
  initialConfig?: AlgoConfig;
  onSave: (config: AlgoConfig) => void;
  onCancel: () => void;
}) {
  const [config, setConfig] = useState<AlgoConfig>(
    initialConfig || { ...DEFAULT_ALGO_CONFIG, id: uuidv4() },
  );
  const [tab, setTab] = useState("ChaseOrder");
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const result = AlgoConfigSchema.safeParse(config);
        if (!result.success) {
          setError(result.error.errors.map((e) => e.message).join(", "));
          return;
        }
        setError(null);
        onSave(config);
      }}
      className="space-y-2 max-w-[98vw] w-full"
    >
      {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
      <div>
        <label className="block mb-0.5 font-medium text-xs">Name</label>
        <Input
          value={config.configName}
          onChange={(e) => setConfig({ ...config, configName: e.target.value })}
          required
          className="h-7 px-2 py-1 text-xs w-full"
        />
      </div>
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-2">
          <TabsTrigger value="ChaseOrder">Chase Order</TabsTrigger>
          <TabsTrigger value="ScaleOrder">Scale Order</TabsTrigger>
          <TabsTrigger value="Grid">Grid</TabsTrigger>
          <TabsTrigger value="ImplementationShortfall">
            Implementation Shortfall
          </TabsTrigger>
        </TabsList>
        <TabsContent value="ChaseOrder">
          <ChaseOrderForm
            value={config.ChaseOrder}
            onChange={(v) => setConfig((c) => ({ ...c, ChaseOrder: v }))}
          />
        </TabsContent>
        <TabsContent value="ScaleOrder">
          <ScaleOrderForm
            value={config.ScaleOrder}
            onChange={(v) => setConfig((c) => ({ ...c, ScaleOrder: v }))}
          />
        </TabsContent>
        <TabsContent value="Grid">
          <GridForm
            value={config.Grid}
            onChange={(v) => setConfig((c) => ({ ...c, Grid: v }))}
          />
        </TabsContent>
        <TabsContent value="ImplementationShortfall">
          <ImplementationShortfallForm
            value={config.ImplementationShortfall}
            onChange={(v) =>
              setConfig((c) => ({ ...c, ImplementationShortfall: v }))
            }
          />
        </TabsContent>
      </Tabs>
      <DialogFooter>
        <Button type="submit" size="xs">
          Save
        </Button>
        <Button type="button" variant="outline" size="xs" onClick={onCancel}>
          Cancel
        </Button>
      </DialogFooter>
    </form>
  );
}

export function Configs() {
  const { user } = useUserStore();
  const userId = user?.uid;
  const {
    configs,
    isLoading,
    hasError,
    loadConfigs,
    addConfig,
    updateConfig,
    deleteConfig,
  } = useConfigsStore();
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AlgoConfig | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importedConfig, setImportedConfig] = useState<AlgoConfig | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (userId) {
      loadConfigs(userId);
    }
  }, [userId, loadConfigs]);

  const handleSave = async (config: AlgoConfig) => {
    if (!userId) {
      return;
    }
    if (editingConfig) {
      await updateConfig(userId, config);
    } else {
      await addConfig(userId, config);
    }
    setShowForm(false);
    setEditingConfig(null);
  };

  const handleDelete = async (id: string) => {
    if (!userId) {
      return;
    }
    await deleteConfig(userId, id);
    setDeletingId(null);
  };

  // Export a single config as JSON
  const handleExport = (cfg: AlgoConfig) => {
    const dataStr = JSON.stringify(cfg, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `algo-config-${cfg.configName || cfg.id}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Import a new config (add)
  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        setImportedConfig(json);
        setShowImportConfirm(true);
      } catch {
        alert("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const handleImportConfirm = async () => {
    if (!userId || !importedConfig) {
      return;
    }
    // Add as new config with a new id
    const newConfig = { ...importedConfig, id: uuidv4() };
    await addConfig(userId, newConfig);
    setShowImportConfirm(false);
    setImportedConfig(null);
    loadConfigs(userId);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2">
          <Button
            size="xxs"
            variant="ghost"
            aria-label="Import"
            className="h-5 w-5 p-0.5"
            onClick={handleImportClick}
          >
            <Download className="w-3.5 h-3.5" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            size="xxs"
            variant="ghost"
            aria-label="New Config"
            className="h-5 w-5 p-0.5"
            onClick={() => {
              setShowForm(true);
              setEditingConfig(null);
            }}
          >
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
      {isLoading && <div>Loading...</div>}
      {hasError && <div className="text-red-600">Error loading configs.</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {configs.map((cfg) => (
            <TableRow key={cfg.id}>
              <TableCell>{cfg.configName}</TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button
                    size="xxs"
                    variant="ghost"
                    aria-label="Export"
                    className="h-5 w-5 p-0.5"
                    onClick={() => handleExport(cfg)}
                  >
                    <Upload className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="xxs"
                    variant="ghost"
                    aria-label="Edit"
                    className="h-5 w-5 p-0.5"
                    onClick={() => {
                      setEditingConfig(cfg);
                      setShowForm(true);
                    }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="xxs"
                        variant="ghost"
                        aria-label="Delete"
                        className="h-5 w-5 p-0.5 text-destructive hover:bg-destructive/10"
                      >
                        <Trash className="w-3.5 h-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Config</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this config?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(cfg.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open);
          if (!open) {
            setEditingConfig(null);
          }
        }}
      >
        <DialogContent className="max-w-[1200px] w-full">
          <DialogHeader>
            <DialogTitle>{editingConfig ? "Edit" : "New"} Config</DialogTitle>
          </DialogHeader>
          <ConfigForm
            initialConfig={editingConfig || undefined}
            onSave={handleSave}
            onCancel={() => {
              setShowForm(false);
              setEditingConfig(null);
            }}
          />
        </DialogContent>
      </Dialog>
      <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import Config</AlertDialogTitle>
            <AlertDialogDescription>
              This will add a new config. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowImportConfirm(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleImportConfirm}>
              Import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
